import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import { UserContext } from "../Contexts/LoginContext";
import { appointmentsApi } from "./appointmentsApi";
import { formatDateTime } from "./appointmentsUiUtils";
import styles from "./AppointmentAttendanceToastWatcher.module.scss";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";

const UNRESOLVED_STATUSES = new Set(["booked", "pending", "confirmed", "rescheduled"]);
const REFRESH_INTERVAL_MS = 120000; // 120 seconds
const EXCLUDED_PATH_PREFIXES = ["/", "/unauthorized", "/book/", "/exhibition-form"];

function shouldRunAttendanceWatcher() {
  const path = window.location.pathname || "/";
  // Exclude login/public pages to avoid unauthorized redirect loops.
  return !EXCLUDED_PATH_PREFIXES.some((prefix) =>
    prefix === "/" ? path === "/" : path.startsWith(prefix)
  );
}

export default function AppointmentAttendanceToastWatcher() {
  const { username } = useContext(UserContext);
  const [queue, setQueue] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toastIdRef = useRef(null);

  const currentAppointment = useMemo(() => queue[currentIndex] || null, [queue, currentIndex]);

  // Fetch appointments and build queue of unresolved, past appointments
  const refreshQueue = useCallback(async () => {
    if (!username || !shouldRunAttendanceWatcher()) {
      setQueue([]);
      setCurrentIndex(0);
      setIsSubmitting(false);
      return;
    }

    try {
      const data = await appointmentsApi.listAppointments({
        counsellor_id: username,
        page: 1,
        per_page: 200, // Consider increasing or paginating if needed
      });
      const appointments = Array.isArray(data?.appointments) ? data.appointments : [];
      const now = new Date();

      const pending = appointments.filter((apt) => {
        if (!UNRESOLVED_STATUSES.has(apt.status)) return false;
        if (!apt.end_time_utc) return false;
        return new Date(apt.end_time_utc) <= now;
      });

      setQueue(pending);
      setCurrentIndex((prev) => (pending.length ? Math.min(prev, pending.length - 1) : 0));
    } catch (error) {
      // Silently fail to avoid spamming toasts – you could log to an external service
      console.error("Failed to refresh attendance queue:", error);
    }
  }, [username]);

  // Mark attendance (attended = true, missed = false)
  const markAttendance = useCallback(
    async (attended) => {
      if (!currentAppointment?.id) return;

      const status = attended ? "attended" : "missed";
      try {
        setIsSubmitting(true);
        const data = await appointmentsApi.updateAppointment(currentAppointment.id, { status });
        if (data?.error) {
          toast.error(data.error);
          setIsSubmitting(false);
          return;
        }

        toast.success(`Meeting marked as ${status}.`);
        // Remove the processed appointment from the queue
        setQueue((prev) => prev.filter((item) => item.id !== currentAppointment.id));
        setCurrentIndex(0); // Reset to first item
      } catch (error) {
        toast.error("Failed to update attendance. Please try again.");
      } finally {
        setIsSubmitting(false);
      }
    },
    [currentAppointment]
  );

  // Refresh queue on username change and set up periodic refresh
  useEffect(() => {
    if (!shouldRunAttendanceWatcher() || !username) {
      setQueue([]);
      setCurrentIndex(0);
      if (toastIdRef.current && toast.isActive(toastIdRef.current)) {
        toast.dismiss(toastIdRef.current);
      }
      toastIdRef.current = null;
      return;
    }

    refreshQueue();

    const intervalId = setInterval(() => {
      if (document.hidden) return;
      refreshQueue();
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [username, refreshQueue]);

  // Handle toast display / update
  useEffect(() => {
    // Dismiss toast if queue is empty
    if (!currentAppointment) {
      if (toastIdRef.current && toast.isActive(toastIdRef.current)) {
        toast.dismiss(toastIdRef.current);
      }
      toastIdRef.current = null;
      return;
    }

    const content = (
      <div className={styles.toast}>
        
{!isSubmitting && (
  <div className={styles.shell}>
    {queue.length > 1 && (
      <div className={styles.leftNavGroup}>
        <Button
          size="sm"
          variant="outline-secondary"
          className={styles.navButton}
          onClick={() => setCurrentIndex((prev) => (prev === 0 ? queue.length - 1 : prev - 1))}
          aria-label="Previous appointment"
        >
          <ChevronLeft />
        </Button>
        
      </div>
    )}
    <HelpOutlineIcon
          className={styles.navHelpOutside}
          title="Navigate between past appointments"
        />

    <div className={styles.center}>
      <div className={styles.title}>Did you attend this meeting?</div>
      <div className={styles.subtitle}>
        {currentAppointment.customer_name || currentAppointment.customer_email} at{' '}
        {formatDateTime(currentAppointment.start_time_utc, currentAppointment.timezone)}
      </div>

      {queue.length > 1 && (
        <div className={styles.pager}>
          {currentIndex + 1} of {queue.length}
        </div>
      )}

      <div className={styles.actions}>
        <Button size="sm" variant="success" onClick={() => markAttendance(true)} disabled={isSubmitting}>
          Yes
        </Button>
        <Button size="sm" variant="danger" onClick={() => markAttendance(false)} disabled={isSubmitting}>
          No
        </Button>
      </div>
    </div>

    {queue.length > 1 && (
      <Button
        size="sm"
        variant="outline-secondary"
        className={styles.navButton}
        onClick={() => setCurrentIndex((prev) => (prev === queue.length - 1 ? 0 : prev + 1))}
        aria-label="Next appointment"
      >
        <ChevronRight />
      </Button>
    )}
  </div>
)}
      </div>
    );

    // Update existing toast or create a new one
    if (toastIdRef.current && toast.isActive(toastIdRef.current)) {
      toast.update(toastIdRef.current, {
        render: content,
        autoClose: false,
        closeOnClick: false,
      });
    } else {
      toastIdRef.current = toast.info(content, {
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        // Optional: add a custom toast ID to avoid duplicates
        toastId: "attendance-watcher",
        icon: false,
      });
    }

  }, [currentAppointment, queue, currentIndex, markAttendance, isSubmitting]);

  // Only dismiss on component unmount.
  useEffect(
    () => () => {
      if (toastIdRef.current && toast.isActive(toastIdRef.current)) {
        toast.dismiss(toastIdRef.current);
      }
    },
    []
  );

  return null;
}